import * as React from 'react'

export const useMediaQuery = (query) => {
  const [matches, setMatches] = React.useState(() => {
    // Initialize with the actual media query result
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches
    }
    return false
  })

  React.useEffect(() => {
    const media = window.matchMedia(query)
    const listener = () => setMatches(media.matches)

    // Set initial value
    setMatches(media.matches)

    // Listen for changes
    media.addEventListener('change', listener)
    return () => media.removeEventListener('change', listener)
  }, [query])

  return matches
}

export const useMediaQueryScreen = (size) => {
  const sizes = {
    sm: '(min-width: 640px)',
    md: '(min-width: 768px)',
    lg: '(min-width: 1024px)',
    xl: '(min-width: 1280px)',
  }

  return useMediaQuery(sizes[size])
}

/**
 * Often you want to know if the screen is desktop or mobile
 *
 * Example usage:
 *  const isDesktop = useIsDesktop()
 *
 * if (isDesktop) {
 *  <DesktopComponent />
 * } else {
 *  <MobileComponent />
 * }
 *
 */
export const useIsDesktop = () => {
  const isMobile = useIsMobile()
  return !isMobile
}

// Device detection based on user agent rather than screen size
export const useIsMobile = () => {
  const [isMobile] = React.useState(() => {
    if (typeof window === 'undefined') return false

    // Check user agent for mobile devices
    const userAgent = window.navigator.userAgent.toLowerCase()
    const mobileRegex =
      /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i
    return mobileRegex.test(userAgent)
  })

  return isMobile
}

export const useIsTablet = () => !useMediaQueryScreen('lg')

/**
 * Hook to detect device orientation (portrait or landscape)
 *
 * Example usage:
 *  const orientation = useOrientation()
 *
 * if (orientation === 'portrait') {
 *  <PortraitLayout />
 * } else {
 *  <LandscapeLayout />
 * }
 */
export const useOrientation = () => {
  const [orientation, setOrientation] = React.useState(() => {
    if (typeof window === 'undefined') return 'portrait'

    return window.innerHeight > window.innerWidth ? 'portrait' : 'landscape'
  })

  React.useEffect(() => {
    const checkOrientation = () => {
      const isPortrait = window.innerHeight > window.innerWidth
      setOrientation(isPortrait ? 'portrait' : 'landscape')
    }

    // Initial check
    checkOrientation()

    // Listen for resize and orientation changes
    window.addEventListener('resize', checkOrientation)
    window.addEventListener('orientationchange', checkOrientation)

    return () => {
      window.removeEventListener('resize', checkOrientation)
      window.removeEventListener('orientationchange', checkOrientation)
    }
  }, [])

  return orientation
}
